/**
 * Hook for managing core portfolio state
 */

import { useState, useEffect } from 'react';
import { getTierColors } from '../../services/TierColorService';

/**
 * Custom hook for managing pinned clients state
 * @returns {Object} Pinned clients state and utility functions
 */
const usePortfolioState = () => {
  // State for pinned clients
  const [pinnedClients, setPinnedClients] = useState([]);
  const [orderedPinnedClients, setOrderedPinnedClients] = useState([]);

  // Utility function to get unique client key
  const getClientKey = (client) => `${client.client_name}-${client.country}`;

  // Check if a client is pinned
  const isPinned = (client) => {
    return pinnedClients.some(pinned => 
      pinned.client_name === client.client_name && pinned.country === client.country
    );
  };

  // Pin/unpin a client
  const togglePinClient = (client) => {
    if (isPinned(client)) {
      // Remove from pinnedClients
      const newPinnedClients = pinnedClients.filter(
        pinned => !(pinned.client_name === client.client_name && pinned.country === client.country)
      );
      setPinnedClients(newPinnedClients);
      
      // Also remove from ordered list
      setOrderedPinnedClients(orderedPinnedClients.filter(
        pinned => !(pinned.client_name === client.client_name && pinned.country === client.country)
      ));
    } else {
      // Get tier-based colors
      const tierColors = getTierColors();
      const clientTier = client['Tier'] || 'Unknown';
      const tierColorData = tierColors[clientTier] || tierColors['Unknown'];
      
      const clientWithColor = {
        ...client,
        cardColor: tierColorData.light,
        cardAccent: tierColorData.dark
      };
      
      const newPinnedClients = [...pinnedClients, clientWithColor];
      setPinnedClients(newPinnedClients);
      
      // Add to the end of ordered list
      setOrderedPinnedClients([...orderedPinnedClients, clientWithColor]);
    }
  };

  // Unpin a specific client
  const unpinClient = (client) => {
    // Remove from pinnedClients
    setPinnedClients(pinnedClients.filter(
      pinned => !(pinned.client_name === client.client_name && pinned.country === client.country)
    ));
    
    // Also remove from ordered list
    setOrderedPinnedClients(orderedPinnedClients.filter(
      pinned => !(pinned.client_name === client.client_name && pinned.country === client.country)
    ));
  };

  // Unpin all clients
  const unpinAllClients = () => {
    setPinnedClients([]);
    setOrderedPinnedClients([]);
  };

  return {
    // State
    pinnedClients,
    orderedPinnedClients,
    
    // Methods
    isPinned,
    togglePinClient,
    unpinClient,
    unpinAllClients,
    
    // Internal utilities
    getClientKey,
    setPinnedClients,
    setOrderedPinnedClients
  };
};

export default usePortfolioState;
